import { useActiveSubscription } from "@/features/subscription/hooks/useActiveSubscription";
import { auth, functions } from "@/libs/firebase";
import { caremeTheme } from "@/libs/theme";
import {
	Backdrop,
	Box,
	Button,
	CircularProgress,
	Link,
	Stack,
	Typography,
} from "@mui/material";
import { Container } from "@mui/system";
import format from "date-fns/format/index.js";
import ja from "date-fns/locale/ja/index.js";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import { type SyntheticEvent, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useHttpsCallable } from "react-firebase-hooks/functions";

export function Cancel() {
	const [user] = useAuthState(auth);

	const { activeSubscription } = useActiveSubscription(user?.uid);

	const [cancelSubscription, _, error] = useHttpsCallable(
		functions,
		"cancelSubscription",
	);

	const { enqueueSnackbar } = useSnackbar();

	const [submitting, setSubmitting] = useState(false);

	const router = useRouter();

	const handleClick = async (e: SyntheticEvent) => {
		e.preventDefault();

		if (!activeSubscription) return;

		try {
			setSubmitting(true);

			await cancelSubscription({ subscriptionId: activeSubscription.id });

			if (error) {
				enqueueSnackbar("解約処理に失敗しました。", {
					variant: "error",
					anchorOrigin: {
						vertical: "top",
						horizontal: "center",
					},
				});
				return;
			}

			enqueueSnackbar("解約が完了しました。", {
				variant: "success",
				anchorOrigin: {
					vertical: "top",
					horizontal: "center",
				},
			});
			router.push("/home");
		} catch (e) {
			enqueueSnackbar("解約処理に失敗しました。", {
				variant: "error",
				anchorOrigin: {
					vertical: "top",
					horizontal: "center",
				},
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Container component="main" maxWidth="xs">
			{activeSubscription ? (
				<Box display="flex" flexDirection="column">
					<Typography textAlign="center" mt={5} px={8.5}>
						ケアミープレミアムをご利用いただきありがとうございました。下のボタンから解約手続きを完了してください。
					</Typography>
					<Box mt={4} mx={6.5}>
						<Button
							fullWidth
							variant="contained"
							onClick={handleClick}
							disabled={submitting}
						>
							{submitting ? <CircularProgress color="primary" /> : "解約する"}
						</Button>

						<Typography textAlign="center" mt={3}>
							{`解約しても ${format(
								activeSubscription.currentPeriodEnd,
								"yyyy年MM月dd日",
								{
									locale: ja,
								},
							)} までは引き続きご利用になれます`}
						</Typography>

						<Backdrop
							sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
							open={submitting}
						>
							<Stack justifyContent="center" alignItems="center">
								<CircularProgress color="primary" />
								<Typography mt={2} color="white">
									解約処理中です...
								</Typography>
							</Stack>
						</Backdrop>
					</Box>
				</Box>
			) : (
				<Box display="flex" flexDirection="column">
					<Typography textAlign="center" mt={5} px={8.5}>
						エラーが発生しました。
					</Typography>
					<Typography mt={3} variant="body2" textAlign="center">
						<Link
							component={NextLink}
							href="/home"
							underline="none"
							typography="subtitle2"
							color={caremeTheme.palette.grey[600]}
						>
							戻る
						</Link>
					</Typography>
				</Box>
			)}
		</Container>
	);
}
